import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { passengerDocumentsApi } from '@/api/passengerDocuments';
import { DocumentUpload } from './DocumentUpload';
import { Download, FileText, Image, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
function formatSize(bytes) {
    if (!bytes)
        return '—';
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function formatDate(dateStr) {
    if (!dateStr)
        return '—';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}
function FileIcon({ mimeType }) {
    if (!mimeType)
        return _jsx(FileText, { className: "w-5 h-5 text-gray-400" });
    if (mimeType.startsWith('image/'))
        return _jsx(Image, { className: "w-5 h-5 text-blue-500" });
    if (mimeType === 'application/pdf')
        return _jsx(FileText, { className: "w-5 h-5 text-red-500" });
    return _jsx(FileText, { className: "w-5 h-5 text-gray-400" });
}
export function PassengerDocsPanel({ passengerId }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);
    const { accessToken } = useAuthStore();
    const load = useCallback(() => {
        setLoading(true);
        passengerDocumentsApi.listByPassenger(passengerId)
            .then(setDocs)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [passengerId]);
    useEffect(() => { load(); }, [load]);
    async function handleDelete(doc) {
        if (!confirm(`¿Eliminar "${doc.fileName}"?`))
            return;
        setDeleting(doc.id);
        try {
            await passengerDocumentsApi.delete(passengerId, doc.id);
            load();
        }
        catch {
            alert('No se pudo eliminar');
        }
        finally {
            setDeleting(null);
        }
    }
    function getDownloadUrl(doc) {
        return passengerDocumentsApi.getDownloadUrl(passengerId, doc.id);
    }
    if (loading) {
        return _jsx("div", { className: "flex justify-center py-8", children: _jsx(Loader2, { className: "w-6 h-6 animate-spin" }) });
    }
    return (_jsxs("div", { className: "flex flex-col gap-6", children: [_jsx(DocumentUpload, { passengerId: passengerId, onUploadSuccess: load }), docs.length === 0 ? (_jsx("div", { className: "text-center py-6 text-sm text-[var(--color-text-muted)]", children: "No hay documentos para este pasajero" })) : (_jsx("div", { className: "space-y-2", children: docs.map(doc => (_jsxs("div", { className: "flex items-center gap-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]", children: [_jsx(FileIcon, { mimeType: doc.mimeType }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-[var(--color-text)] truncate", children: doc.fileName }), _jsxs("div", { className: "flex items-center gap-3 text-xs text-[var(--color-text-muted)] mt-0.5", children: [_jsx("span", { children: doc.documentType?.name ?? 'Documento' }), _jsx("span", { children: formatSize(doc.size) }), _jsxs("span", { children: ["Subido ", formatDate(doc.uploadedAt)] }), doc.lastDownloadAt && (_jsxs("span", { children: ["\u00DAltima descarga ", formatDate(doc.lastDownloadAt)] }))] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("a", { href: getDownloadUrl(doc), target: "_blank", rel: "noreferrer", className: "p-1.5 rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-surface-raised)]", title: "Descargar", children: _jsx(Download, { className: "w-4 h-4" }) }), doc.url.startsWith('http') && (_jsx("a", { href: doc.url, target: "_blank", rel: "noreferrer", className: "p-1.5 rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-surface-raised)]", title: "Abrir enlace", children: _jsx(ExternalLink, { className: "w-4 h-4" }) })), _jsx("button", { className: "p-1.5 rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-error-light)] hover:text-[var(--color-error)]", onClick: () => handleDelete(doc), disabled: deleting === doc.id, title: "Eliminar", children: _jsx(Trash2, { className: "w-4 h-4" }) })] })] }, doc.id))) }))] }));
}
//# sourceMappingURL=PassengerDocsPanel.js.map